/**
 * Arrastar sem re-renderizar.
 *
 * O que torna um widget flutuante pesado não é existir, é como ele se move: um
 * `setState` por `pointermove` re-renderiza a árvore sessenta vezes por segundo
 * enquanto o dedo está na tela, e é isso que trava o canvas do flow ou a lista
 * do Inbox por baixo.
 *
 * Aqui o movimento é escrito direto no nó, em `transform`, que o navegador
 * compõe na GPU sem recalcular layout. O React só fica sabendo no `pointerup`,
 * uma vez, para gravar a posição final.
 *
 * O ponteiro é capturado, então o arrasto sobrevive a passar por cima de um
 * iframe, do canvas do ReactFlow ou de qualquer área que normalmente roubaria
 * os eventos.
 */

import { useCallback, useEffect, useRef } from "react";

/** Abaixo disto foi clique, não arrasto. Evita engolir o toque de quem só quis abrir. */
const DRAG_THRESHOLD_PX = 4;

export default function useDraggable({ position, onCommit, disabled = false }) {
  const nodeRef = useRef(null);
  const stateRef = useRef(null);
  // Lido pelo `onClick` logo depois do `pointerup`: sem isto, soltar a bolinha
  // no lugar novo também abriria o painel.
  const draggedRef = useRef(false);

  const handlePointerDown = useCallback(
    (event) => {
      if (disabled) return;
      // Só o botão primário arrasta: o secundário é menu de contexto.
      if (event.button !== 0) return;

      const node = nodeRef.current;
      if (!node) return;

      // A captura NÃO acontece aqui: ver o comentário em `handlePointerMove`.
      stateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        origin: position,
        moved: false,
      };
      draggedRef.current = false;
    },
    [disabled, position],
  );

  const handlePointerMove = useCallback((event) => {
    const state = stateRef.current;
    const node = nodeRef.current;
    if (!state || !node || state.pointerId !== event.pointerId) return;

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;

    if (!state.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      state.moved = true;
      draggedRef.current = true;
      /**
       * A captura começa AQUI, não no `pointerdown`.
       *
       * Enquanto um elemento tem o ponteiro capturado, o navegador redireciona
       * os eventos seguintes para ele, e o `click` sintetizado passa a nascer
       * nesse elemento em vez de no botão que o usuário apertou. Capturando no
       * `pointerdown`, um clique simples deixava de acionar o `onClick` do
       * botão: a bolinha não abria.
       *
       * Capturar só quando o arrasto realmente começa resolve os dois lados.
       * O clique parado nunca envolve captura e funciona normalmente; o arrasto
       * captura e sobrevive a passar por cima do canvas do ReactFlow, e o
       * clique que vem depois dele já é descartado por `consumeDrag`.
       *
       * O jsdom não implementa esse redirecionamento, então nenhum teste de
       * unidade pega a regressão: o que a trava é o teste abaixo, que afirma
       * que um clique parado não captura nada.
       */
      node.setPointerCapture?.(event.pointerId);

      /**
       * Arrastar não pode selecionar a página.
       *
       * Sem isto, puxar a bolinha por cima da tela pinta de azul tudo que o
       * ponteiro atravessa, e a seleção fica lá depois de soltar. `select-none`
       * no próprio nó não resolve: quem está sendo selecionado é o conteúdo
       * ABAIXO dele.
       */
      document.body.style.userSelect = "none";
    }
    if (!state.moved) return;

    // Direto no nó, de propósito: ver o comentário do topo.
    node.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  }, []);

  const endDrag = useCallback(
    (event) => {
      const state = stateRef.current;
      const node = nodeRef.current;
      if (!state || !node || state.pointerId !== event.pointerId) return;

      stateRef.current = null;
      // Só houve captura e bloqueio de seleção se houve arrasto.
      if (state.moved) {
        node.releasePointerCapture?.(event.pointerId);
        document.body.style.userSelect = "";
      }
      node.style.transform = "";

      if (!state.moved) return;

      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      // `right`/`bottom` crescem para a esquerda e para cima: o sinal inverte.
      onCommit({
        right: state.origin.right - dx,
        bottom: state.origin.bottom - dy,
      });
    },
    [onCommit],
  );

  // Desmontar no meio de um arrasto (uma navegação, por exemplo) deixaria a
  // página inteira sem poder selecionar texto, para sempre.
  useEffect(() => () => {
    document.body.style.userSelect = "";
  }, []);

  /** `true` quando o gesto que acabou foi arrasto, para o clique se anular. */
  const consumeDrag = useCallback(() => {
    const dragged = draggedRef.current;
    draggedRef.current = false;
    return dragged;
  }, []);

  return {
    nodeRef,
    consumeDrag,
    dragProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
